<template>
  <div class="landing-page">
    <!-- Header Navigation -->
    <header class="header">
      <nav class="navbar">
        <div class="nav-brand">
          <img src="/favicon.svg" alt="ADI Logo" class="brand-icon">
          <span class="brand-logo">ADI</span>
          <span class="brand-text">Actionable Document Intelligence</span>
        </div>
        <div class="nav-links">
          <a href="#platform" class="nav-link">Platform</a>
          <a href="#how-it-works" class="nav-link">How It Works</a>
          <router-link to="/clients" class="nav-link">Clients</router-link>
          <router-link to="/login" class="btn btn-primary">Login</router-link>
        </div>
      </nav>
    </header>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">Transform Your Documents into Actionable Intelligence</h1>
        <p class="hero-subtitle">
          Documents are the new data—every one holds valuable knowledge. We unlock that knowledge 
          and turn it into clear actions that drive growth, boost efficiency, and keep you compliant.
        </p>
        <div class="hero-actions">
          <button @click="openCalendly" class="btn btn-primary btn-large">See Platform Demo</button>
          <a href="#how-it-works" class="btn btn-secondary btn-large">Learn More</a>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section id="how-it-works" class="section">
      <div class="container">
        <h2 class="section-title">From Documents to Growth</h2>
        <p class="section-subtitle">
          ADI transforms your document chaos into structured, actionable intelligence that your team can trust and act upon.
        </p>
        
        <div class="process-steps">
          <div class="process-step">
            <div class="step-icon">📄</div>
            <h3>Documents → Knowledge</h3>
            <p>Our AI-powered platform processes any document type, extracting meaningful insights using advanced LLM and SLM technologies.</p>
          </div>
          <div class="process-step">
            <div class="step-icon">🧠</div>
            <h3>Knowledge → Actions</h3>
            <p>UTH/INK™ cognitive curation model transforms extracted knowledge into specific, actionable recommendations your team can implement.</p>
          </div>
          <div class="process-step">
            <div class="step-icon">📈</div>
            <h3>Actions → Growth</h3>
            <p>Implemented actions drive measurable improvements in efficiency, compliance, and business growth across your organization.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Platform Features Section -->
    <section id="platform" class="section section-alt">
      <div class="container">
        <h2 class="section-title">The ADI Platform</h2>
        
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-number">1</div>
            <h3>Intelligent Rule Packs</h3>
            <p>Customizable rule sets tailored to your specific culture, workflows, and business goals—understanding exactly how your organization operates.</p>
          </div>
          <div class="feature-card">
            <div class="feature-number">2</div>
            <h3>AI-Powered Processing</h3>
            <p>Advanced LLM and SLM technologies work together to understand context, extract insights, and identify patterns.</p>
          </div>
          <div class="feature-card">
            <div class="feature-number">3</div>
            <h3>UTH/INK™ Curation</h3>
            <p>Our proprietary human cognitive curation model ensures accuracy and relevance in every recommendation.</p>
          </div>
          <div class="feature-card">
            <div class="feature-number">4</div>
            <h3>Full Transparency</h3>
            <p>See exactly how decisions were made with complete rule traceability, enabling your team to become curators themselves.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2 class="cta-title">Ready to Transform Your Documents?</h2>
        <p class="cta-subtitle">
          Join industry leaders who trust ADI to turn their document chaos into competitive advantage.
        </p>
        <button @click="openCalendly" class="btn btn-primary btn-large">Schedule Demo</button>
      </div>
    </section>

    <!-- Industries & Technology Section -->
    <section class="section">
      <div class="container">
        <h2 class="section-title">Powering Success Across Industries</h2>
        <p class="section-subtitle">Advanced AI technology delivering measurable results for diverse business needs</p>
        
        <!-- Use Cases Grid -->
        <div class="use-cases-grid">
          <div class="use-case-card">
            <div class="use-case-icon">🏥</div>
            <h4>Insurance</h4>
            <p>Claims processing & risk assessment workflows</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">🏠</div>
            <h4>Real Estate</h4>
            <p>Contract analysis & due diligence automation</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">⚖️</div>
            <h4>Legal</h4>
            <p>Document review & compliance monitoring</p>
          </div>
          <div class="use-case-card">
            <div class="use-case-icon">🩺</div>
            <h4>Healthcare</h4>
            <p>Medical record analysis & reporting</p>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="stats-section">
          <div class="stat-item">
            <div class="stat-number">75K</div>
            <div class="stat-label">Documents Processed in 2025</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">95%</div>
            <div class="stat-label">Reduction in Manual Review</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">15</div>
            <div class="stat-label">Data Integrations</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">99.9%</div>
            <div class="stat-label">Accuracy Rate</div>
          </div>
        </div>

        <!-- Technology Highlights -->
        <div class="tech-highlights">
          <h3 class="tech-title">Powered by Advanced AI</h3>
          <div class="tech-grid">
            <div class="tech-item">
              <div class="tech-icon">🧠</div>
              <h5>Large Language Models</h5>
              <p>Deep understanding and context analysis</p>
            </div>
            <div class="tech-item">
              <div class="tech-icon">⚡</div>
              <h5>Small Language Models</h5>
              <p>Efficient processing for speed and scale</p>
            </div>
            <div class="tech-item">
              <div class="tech-icon">🎯</div>
              <h5>UTH/INK™ Curation</h5>
              <p>Human cognitive model for accuracy</p>
            </div>
            <div class="tech-item">
              <div class="tech-icon">🔒</div>
              <h5>Enterprise Security</h5>
              <p>Bank-grade security and compliance</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="final-cta-section">
      <div class="container">
        <h2 class="cta-title">Ready to Turn Your Documents into Growth?</h2>
        <p class="cta-subtitle">
          Discover how ADI can transform your documents into actionable intelligence that drives competitive advantages.
        </p>
        <div class="cta-actions">
          <button @click="openCalendly" class="btn btn-primary btn-large">Schedule Your Demo</button>
          <router-link to="/clients" class="btn btn-secondary btn-large">Contact Sales</router-link>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <div class="footer-brand">
              <img src="/favicon.svg" alt="ADI Logo" class="brand-icon">
              <span class="brand-logo">ADI</span>
              <span class="brand-text">Actionable Document Intelligence</span>
            </div>
            <p>Unlocking actionable intelligence from every document to drive business growth.</p>
          </div>
          
          <div class="footer-section">
            <h4>Platform</h4>
            <ul>
              <li><a href="#platform">Features</a></li>
              <li><a href="#how-it-works">UTH/INK Cognitive Curation</a></li>
              <li><router-link to="/projects">Integrations</router-link></li>
              <li><router-link to="/settings">Security</router-link></li>
            </ul>
          </div>
          
          <div class="footer-section">
            <h4>Company</h4>
            <ul>
              <li><a href="#about">About</a></li>
              <li><a href="#careers">Careers</a></li>
              <li><a href="#news">News</a></li>
              <li><router-link to="/clients">Contact</router-link></li>
            </ul>
          </div>
          
          <div class="footer-section">
            <h4>Support</h4>
            <ul>
              <li><a href="#docs">Documentation</a></li>
              <li><a href="#help">Help Center</a></li>
              <li><a href="#community">Community</a></li>
              <li><a href="#status">Status</a></li>
            </ul>
          </div>
          
          <div class="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><router-link to="/subprocessors">Subprocessors</router-link></li>
              <li><a href="#privacy">Privacy Policy</a></li>
              <li><a href="#terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        
        <div class="footer-bottom">
          <p>&copy; 2024 ADI - Actionable Document Intelligence. UTH/INK™ is a trademark of ADI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import api from '@/services/api';

const FALLBACK_DEMO_URL = process.env.VUE_APP_CALENDLY_URL
  || 'https://calendly.com/brad-coi-verify/30min?utm_source=adi_home';

export default {
  name: 'HomePage',
  data() {
    return {
      demoUrl: FALLBACK_DEMO_URL,
    };
  },
  mounted() {
    // Load Calendly widget script
    this.loadCalendlyScript();
    this.loadDemoUrl();
    
    // Smooth scrolling for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  },
  methods: {
    loadCalendlyScript() {
      // Check if Calendly script is already loaded
      if (window.Calendly) {
        return;
      }
      
      // Load Calendly widget script
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
      
      // Load Calendly CSS
      const link = document.createElement('link');
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    },
    async loadDemoUrl() {
      // The scheduling service owns the demo link so bookings land as hot leads.
      try {
        const { data } = await api.get('/scheduling/booking-link/', {
          params: { utm_source: 'adi_home', utm_medium: 'web' },
        });
        if (data?.url) this.demoUrl = data.url;
      } catch {
        // Keep the fallback link
      }
    },
    openCalendly() {
      // Check if Calendly is loaded
      if (window.Calendly) {
        window.Calendly.initPopupWidget({ url: this.demoUrl });
      } else {
        // Fallback: open in new tab if script hasn't loaded yet
        window.open(this.demoUrl, '_blank');
      }
    }
  }
}
</script>

<style scoped>
.landing-page {
  margin: -2rem;
  width: calc(100% + 4rem);
  background: var(--color-background);
}

/* Header */
.header {
  background: var(--color-background);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 100;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.brand-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.brand-logo {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--color-primary);
}

.brand-text {
  font-size: 1rem;
  color: var(--color-text);
  font-weight: 500;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-link {
  color: var(--color-text);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-link:hover {
  color: var(--color-primary);
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, var(--color-primary) 0%, #2c4a6b 100%);
  color: white;
  padding: 6rem 2rem;
  text-align: center;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  opacity: 0.9;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  background: none;
  font-family: inherit;
  font-size: inherit;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
}

.btn-primary:hover {
  background: #008f5a;
  transform: translateY(-2px);
}

.btn-secondary {
  background: transparent;
  color: white;
  border-color: white;
}

.btn-secondary:hover {
  background: white;
  color: var(--color-primary);
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.1rem;
}

/* Sections */
.section {
  padding: 5rem 2rem;
}

.section-alt {
  background: var(--color-surface);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: var(--color-primary);
}

.section-subtitle {
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: var(--color-text-light);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Process Steps */
.process-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
}

.process-step {
  text-align: center;
  padding: 2rem;
}

.step-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.process-step h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-primary);
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  border: 1px solid var(--color-border);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-number {
  display: inline-block;
  background: var(--color-accent);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.feature-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-primary);
}

/* CTA Sections */
.cta-section, .final-cta-section {
  background: var(--color-primary);
  color: white;
  padding: 5rem 2rem;
  text-align: center;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-subtitle {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* Use Cases Grid */
.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  margin-bottom: 4rem;
}

.use-case-card {
  background: white;
  padding: 2rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  text-align: center;
  border: 1px solid var(--color-border);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.use-case-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.use-case-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.use-case-card h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--color-primary);
}

/* Stats Section */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
  padding: 3rem 0;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-accent);
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1rem;
  color: var(--color-text-light);
  font-weight: 500;
}

/* Technology Highlights */
.tech-highlights {
  margin-top: 4rem;
}

.tech-title {
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  color: var(--color-primary);
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.tech-item {
  text-align: center;
  padding: 1.5rem;
}

.tech-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.tech-item h5 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--color-primary);
}

.tech-item p {
  font-size: 0.9rem;
  color: var(--color-text-light);
  margin: 0;
}

/* Footer */
.footer {
  background: var(--color-primary);
  color: white;
  padding: 3rem 2rem 1rem;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.footer-brand .brand-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.footer-section h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.footer-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-section ul li {
  margin-bottom: 0.5rem;
}

.footer-section ul li a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-section ul li a:hover {
  color: white;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 1rem;
  text-align: center;
}

.footer-bottom p {
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .nav-links {
    gap: 1rem;
  }
  
  .navbar {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .process-steps,
  .use-cases-grid,
  .tech-grid {
    grid-template-columns: 1fr;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .section {
    padding: 3rem 1rem;
  }
  
  .hero-section {
    padding: 4rem 1rem;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .stat-number {
    font-size: 2.5rem;
  }
}
</style> 